import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, PenLine } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const POSTS = [
  {
    slug: "personalized-gift-guide",
    title: "The Art of Personalized Gifting: 5 Ideas That Always Land",
    date: "Sep 2026",
    tag: "Gift Guides",
    excerpt: "Names, dates and photos turn ordinary objects into keepsakes. Here are five personalized gifts that never miss.",
    body: "A great personalized gift says 'I thought about you specifically'. Our never-fail list: 1) A custom name mug for the coffee obsessive. 2) A photo frame capturing a shared milestone. 3) A cushion printed with an inside joke. 4) A memory scrapbook for anniversaries. 5) A monogrammed keepsake box for the person who has everything. The trick is matching the personalization to a moment only the two of you understand.",
  },
  {
    slug: "photo-printing-tips",
    title: "How to Pick the Perfect Photo for Printing",
    date: "Sep 2026",
    tag: "Tips",
    excerpt: "The difference between a blurry print and a frame-worthy one is usually the photo you start with.",
    body: "Start with the largest, sharpest original you have — screenshots and chat-app forwards are compressed and print poorly. Natural light beats flash. Leave a little space around faces so cropping for mugs and cushions doesn't cut anyone out. And when in doubt, upload it anyway: our print team checks every photo and will tell you if it won't print well before we make your gift.",
  },
  {
    slug: "corporate-gifting-that-works",
    title: "Corporate Gifting That People Actually Keep",
    date: "Sep 2026",
    tag: "Corporate",
    excerpt: "The shelf life of a corporate gift is measured in days — unless it's personal.",
    body: "Most corporate gifts end up in a drawer. The ones that survive share three traits: they're useful (a great mug beats a paperweight), they're well-made, and they carry a touch of personalization beyond just a logo — a name, a team, a milestone. For bulk orders, write to customizegiftonline@gmail.com with your quantities and we'll prepare a quote with digital proofs before production.",
  },
];

export default function BlogPage() {
  const [open, setOpen] = useState(null);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12 lg:py-16" data-testid="blog-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]">Blog</p>
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 mt-3 tracking-tight">Notes on thoughtful gifting</h1>
      <p className="text-stone-500 mt-2">Guides, ideas and behind-the-scenes from our print studio.</p>

      <div className="mt-10 space-y-4">
        {POSTS.map((p) => {
          const isOpen = open === p.slug;
          return (
            <article key={p.slug} className="rounded-2xl bg-white border border-[#E9E2D7] overflow-hidden" data-testid={`blog-post-${p.slug}`}>
              <button onClick={() => setOpen(isOpen ? null : p.slug)} className="w-full flex items-start justify-between gap-4 p-6 text-left" aria-expanded={isOpen} data-testid={`blog-toggle-${p.slug}`}>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#B45309]">{p.tag} · {p.date}</p>
                  <h2 className="font-serif text-xl font-bold text-stone-900 mt-1.5">{p.title}</h2>
                  <p className="text-sm text-stone-500 mt-2">{p.excerpt}</p>
                </div>
                <ChevronDown size={18} className={`text-[#B45309] shrink-0 mt-1 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                    <p className="px-6 pb-6 text-sm text-stone-600 leading-relaxed border-t border-stone-100 pt-4">{p.body}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </article>
          );
        })}
      </div>

      <div className="mt-10 p-6 rounded-2xl bg-[#F9F3EA] border border-[#E9E2D7] flex items-center gap-4">
        <PenLine size={20} className="text-[#B45309] shrink-0" />
        <p className="text-sm text-stone-600">More stories coming soon. Have a topic in mind? <Link to="/contact" className="font-bold text-[#B45309] hover:underline" data-testid="blog-contact-link">Tell us</Link>.</p>
      </div>
    </div>
  );
}
